import React from "react";
import { Zap } from "lucide-react";
import ReactMarkdown from "react-markdown";
import AdminAPI from "../../services/AdminAPI";
import { formatWhen } from "../../Components/approvals/meta";

const Chip = ({ children }) => (
  <span className="mr-1.5 mt-1 inline-block rounded-full border border-[#d9e7f7] bg-white px-2 py-0.5 text-[11px] text-[#1565c0]">{children}</span>
);

const KIND_LABEL = { foundational: "Foundational", middle: "Middle", preparatory: "Preparatory", secondary: "Secondary" };

const MD = {
  h1: (p) => <h3 className="mb-1 mt-3 text-[16px] font-bold text-slate-900" {...p} />,
  h2: (p) => <h3 className="mb-1 mt-3 text-[15px] font-bold text-slate-900" {...p} />,
  h3: (p) => <h4 className="mb-1 mt-3 text-[14px] font-semibold text-slate-900" {...p} />,
  h4: (p) => <h5 className="mb-1 mt-2.5 text-[13px] font-semibold text-[#1565c0]" {...p} />,
  h5: (p) => <h6 className="mb-1 mt-2 text-[13px] font-semibold text-slate-800" {...p} />,
  p: (p) => <p className="my-1.5 text-[13px] leading-relaxed text-slate-800" {...p} />,
  ul: (p) => <ul className="my-1.5 list-disc space-y-0.5 pl-5 text-[13px] text-slate-800" {...p} />,
  ol: (p) => <ol className="my-1.5 list-decimal space-y-0.5 pl-5 text-[13px] text-slate-800" {...p} />,
  hr: () => <hr className="my-3 border-[#e6edf8]" />,
  strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
  blockquote: (p) => <blockquote className="my-2 border-l-4 border-slate-300 pl-3 italic text-slate-600" {...p} />,
  code: (p) => <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]" {...p} />,
};
// Plain-text name: markdown markers and a leading "Activity Title:" label removed.
const cleanName = (t) => String(t || "").replace(/^[\s#>*_-]+/, "").replace(/\s#+\s/g, " ").replace(/#{1,6}\s*/g, "").replace(/\*\*|__/g, "")
  .replace(/(activity\s*title|title)\s*:\s*/i, "").replace(/\s+/g, " ").trim();
const Markdown = ({ text }) => <ReactMarkdown components={MD}>{String(text)}</ReactMarkdown>;

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-[#e6edf8] bg-white p-3">
    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{title}</div>
    {children}
  </div>
);

function ActivityContent({ detail }) {
  const a = detail.activity || {};
  const goals = a.curricular_goals || [];
  const comps = a.competencies || [];
  const questions = a.assessment_questions || a.questions || [];
  const rubric = a.assessment_rubric || [];
  const tf = (a.part_b && a.part_b.page_1 && a.part_b.page_1.teacher_fields) || {};
  const tfc = (a.part_c && a.part_c.page_1 && a.part_c.page_1.teacher_fields) || {};
  return (
    <div className="space-y-3">
      <Section title="Overview">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["Kind", KIND_LABEL[a.activity_kind] || a.activity_kind], ["Domain", a.domain], ["Class", detail.summary?.class_name], ["Students", detail.summary?.student_scope], ["Due", a.due_date ? formatWhen(a.due_date) : ""]].map(([k, v]) => (
            <div key={k}><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{k}</div><div className="text-[13px] text-slate-800">{v || "—"}</div></div>
          ))}
        </div>
        {a.title && <h3 className="mb-0 mt-3 text-[15px] font-bold text-slate-900">{a.title}</h3>}
        {a.description && <div className="mt-1 text-slate-600"><Markdown text={a.description} /></div>}
      </Section>
      {a.activity && <Section title="Activity"><div className="-mt-1"><Markdown text={a.activity} /></div></Section>}
      {(goals.length > 0 || comps.length > 0) && (
        <Section title="Goals and competencies">
          {goals.map((g, i) => <Chip key={`g${i}`}>{g}</Chip>)}
          {comps.map((c, i) => <Chip key={`c${i}`}>{typeof c === "string" ? c : c.name || JSON.stringify(c)}</Chip>)}
        </Section>
      )}
      {(tf.subjects || tf.competencies || tf.project_prompt) && (
        <Section title="Teacher fields (Part B, page 1)">
          {(tf.subjects || []).map((s, i) => <Chip key={`s${i}`}>{s}</Chip>)}
          {(tf.competencies || []).map((s, i) => <Chip key={`tc${i}`}>{typeof s === "string" ? s : s.name || JSON.stringify(s)}</Chip>)}
          {tf.project_prompt && <p className="mb-0 mt-2 whitespace-pre-wrap text-[13px] text-slate-800">{tf.project_prompt}</p>}
        </Section>
      )}
      {(tfc.research_prompt || tfc.hypothesis) && (
        <Section title="Teacher fields (Part C, page 1)">
          {tfc.research_prompt && <p className="m-0 whitespace-pre-wrap text-[13px] text-slate-800"><b>Research prompt:</b> {tfc.research_prompt}</p>}
          {tfc.hypothesis && <p className="mb-0 mt-2 whitespace-pre-wrap text-[13px] text-slate-800"><b>Hypothesis:</b> {tfc.hypothesis}</p>}
        </Section>
      )}
      {questions.length > 0 && (
        <Section title={`Assessment questions (${questions.length})`}>
          <ol className="m-0 space-y-1 pl-5 text-[13px] text-slate-800">{questions.map((q, i) => <li key={i}>{typeof q === "string" ? q : q.question || JSON.stringify(q)}</li>)}</ol>
        </Section>
      )}
      {rubric.length > 0 && (
        <Section title="Rubric">
          <div className="space-y-1.5 text-[12px]">
            {rubric.map((r, i) => (
              <div key={i} className="rounded-lg bg-slate-50 p-2">
                <div className="font-semibold text-slate-800">{r.criterion}</div>
                <div className="text-slate-600">Stream: {r.stream || "—"} · Mountain: {r.mountain || "—"} · Sky: {r.sky || "—"}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

export const ACTIVITY_APPROVALS_CONFIG = {
  entityTitle: "Activities",
  title: "Activity Approvals",
  subtitle: "Review teacher activities before students can see them",
  entityLabel: "activity",
  entityKey: "activity",
  icon: Zap,
  api: {
    queue: (p) => AdminAPI.approvalsQueue("activities", p),
    detail: (id) => AdminAPI.approvalsDetail("activities", id),
    review: (id, body) => AdminAPI.approvalsReview("activities", id, body),
    bulk: (body) => AdminAPI.approvalsBulk("activities", body),
    unpublish: (id) => AdminAPI.approvalsUnpublish("activities", id),
  },
  titleOf: (r) => cleanName(r.title) || "Activity",
  subtitleOf: (r) => [KIND_LABEL[r.activity_kind], r.class_name, r.domain].filter(Boolean).join(" · ") || "Activity",
  columns: [
    { key: "title", label: "Activity", render: (r) => <span className="font-semibold text-slate-800">{r.title}</span> },
    { key: "kind", label: "Kind / domain", render: (r) => [KIND_LABEL[r.activity_kind], r.domain].filter(Boolean).join(" · ") || "—" },
    { key: "class", label: "Class", render: (r) => r.class_name || "—" },
    { key: "teacher", label: "Teacher", render: (r) => r.teacher_name || <span className="text-slate-400">Unknown</span> },
  ],
  renderContent: (detail) => <ActivityContent detail={detail} />,
};
